import { Info, TrendingUp } from "lucide-react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { Card, CardContent } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart"
import { Empty } from "@/components/ui/empty"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatCurrency } from "@/lib/utils"
import type { TimePeriod } from "@/types"

export interface RevenueChartData {
  current: { label: string; revenue: number; profit: number }[]
  current_revenue_cents: number
  previous_revenue_cents: number
  current_profit_cents: number
  previous_profit_cents: number
}

interface RevenueChartProps {
  data: RevenueChartData
  timePeriod: TimePeriod
  onTimePeriodChange: (period: TimePeriod) => void
}

const chartConfig: ChartConfig = {
  revenue: { label: "Revenue", color: "var(--color-blue-500)" },
  profit: { label: "Profit", color: "var(--color-green-500)" },
}

function fmtTick(cents: number) {
  const dollars = cents / 100
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(0)}k`
  return `$${Math.round(dollars)}`
}

function fmtTooltip(cents: number) {
  return "$" + Math.round(cents / 100).toLocaleString()
}

function fmtVs(previous: number) {
  if (previous === 0) return null
  return `vs. ${formatCurrency(previous / 100)} last period`
}

function TimePeriodSelect({
  value,
  onChange,
}: {
  value: TimePeriod
  onChange: (v: TimePeriod) => void
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TimePeriod)}>
      <SelectTrigger className="h-8 w-auto rounded-sm px-3 text-sm font-medium [&_svg]:size-3">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="24_hours">Last 24 hours</SelectItem>
        <SelectItem value="7_days">Last 7 days</SelectItem>
        <SelectItem value="30_days">Last 30 days</SelectItem>
        <SelectItem value="3_months">Last 3 months</SelectItem>
        <SelectItem value="12_months">Last 12 months</SelectItem>
      </SelectContent>
    </Select>
  )
}

export function RevenueChart({
  data,
  timePeriod,
  onTimePeriodChange,
}: RevenueChartProps) {
  if (data.current.length === 0) {
    return (
      <Card className="rounded-sm">
        <CardContent>
          <div className="flex justify-end">
            <TimePeriodSelect
              value={timePeriod}
              onChange={onTimePeriodChange}
            />
          </div>
          <Empty
            className="py-16"
            icon={<TrendingUp className="h-5 w-5" />}
            title="No revenue yet"
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="rounded-sm">
      <CardContent>
        <div className="flex flex-col gap-4">
          {/* Legend + values header. Wraps rather than overflowing: the two
              text-3xl figures plus the period select don't fit a phone-width
              card, and large currency values can outgrow it on their own. */}
          <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap gap-6 sm:gap-10">
              <div>
                <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                  <span className="inline-block h-0.5 w-4 bg-blue-500" />
                  Revenue
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-3.5 cursor-default" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-56">
                      Total amount billed to accounts in the selected period.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="mt-4 text-3xl font-normal tracking-tight">
                  {formatCurrency(data.current_revenue_cents / 100)}
                </div>
                {fmtVs(data.previous_revenue_cents) && (
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">
                      {fmtVs(data.previous_revenue_cents)}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                  <span className="inline-block h-0.5 w-4 bg-green-500" />
                  Profit
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-3.5 cursor-default" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-56">
                      Revenue minus wholesale costs in the selected period.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="mt-4 text-3xl font-normal tracking-tight">
                  {formatCurrency(data.current_profit_cents / 100)}
                </div>
                {fmtVs(data.previous_profit_cents) && (
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">
                      {fmtVs(data.previous_profit_cents)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <TimePeriodSelect
              value={timePeriod}
              onChange={onTimePeriodChange}
            />
          </div>

          {/* Chart */}
          <ChartContainer config={chartConfig} className="h-[250px] w-full">
            <AreaChart
              data={data.current}
              margin={{ top: 8, right: 10, left: -10, bottom: 0 }}
            >
              <defs>
                <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--color-blue-500)"
                    stopOpacity={0.15}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--color-blue-500)"
                    stopOpacity={0}
                  />
                </linearGradient>
                <linearGradient id="fillProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--color-green-500)"
                    stopOpacity={0.15}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--color-green-500)"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval="preserveStartEnd"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={fmtTick}
                tick={{ fontSize: 12 }}
              />
              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  return (
                    <div className="border-border/50 bg-background min-w-[10rem] rounded-lg border px-3 py-2 text-xs shadow-md">
                      <div className="mb-1 font-medium">{label}</div>
                      {payload.map((p) => (
                        <div
                          key={p.dataKey as string}
                          className="flex items-center justify-between gap-4"
                        >
                          <span className="text-muted-foreground capitalize">
                            {p.dataKey as string}
                          </span>
                          <span className="font-medium tabular-nums">
                            {fmtTooltip(p.value as number)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="var(--color-blue-500)"
                strokeWidth={2}
                fill="url(#fillRevenue)"
              />
              <Area
                type="monotone"
                dataKey="profit"
                stroke="var(--color-green-500)"
                strokeWidth={2}
                fill="url(#fillProfit)"
              />
            </AreaChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  )
}
